/**
 * Client d'API.
 *
 * Le jeton d'accès vit quinze minutes et reste en mémoire — jamais dans le
 * stockage du navigateur, qu'une injection de script saurait lire. Le jeton de
 * renouvellement est dans un cookie que le JavaScript ne voit pas ; c'est le
 * navigateur qui l'envoie, et lui seul.
 *
 * Quand une requête revient en 401, le client renouvelle une fois et rejoue la
 * requête. Un seul renouvellement à la fois : dix écrans qui se réveillent
 * ensemble après une mise en veille ne doivent pas déclencher dix rotations de
 * jeton, dont neuf seraient prises pour un vol et couperaient la session.
 */

let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;

type Listener = () => void;
const sessionLostListeners = new Set<Listener>();

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Prévient l'application que la session est finie, pour revenir à la connexion. */
export function onSessionLost(listener: Listener): () => void {
  sessionLostListeners.add(listener);
  return () => sessionLostListeners.delete(listener);
}

function announceSessionLost(): void {
  accessToken = null;
  for (const listener of sessionLostListeners) listener();
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function refreshSession(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) return false;
      const payload = (await response.json()) as { accessToken: string };
      accessToken = payload.accessToken;
      return true;
    } catch {
      return false;
    } finally {
      // Libéré au tour de boucle suivant, pour que les appels concurrents
      // arrivés pendant la requête partagent bien ce résultat-ci.
      queueMicrotask(() => {
        refreshing = null;
      });
    }
  })();

  return refreshing;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Identifiant d'établissement, ajouté en en-tête pour les routes qui l'attendent. */
  hospitalId?: string | null;
  signal?: AbortSignal;
  /** Ne pas tenter de renouveler : utilisé par la connexion elle-même. */
  noRetry?: boolean;
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = {};
    if (options.body !== undefined) headers['content-type'] = 'application/json';
    if (accessToken !== null) headers['authorization'] = `Bearer ${accessToken}`;
    if (options.hospitalId != null) headers['x-hospital-id'] = options.hospitalId;

    return fetch(path, {
      method: options.method ?? 'GET',
      headers,
      credentials: 'include',
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      ...(options.signal === undefined ? {} : { signal: options.signal }),
    });
  };

  let response = await send();

  if (response.status === 401 && options.noRetry !== true) {
    const renewed = await refreshSession();
    if (!renewed) {
      announceSessionLost();
      throw new ApiError(401, 'Session terminée. Reconnectez-vous.');
    }
    response = await send();
    if (response.status === 401) {
      announceSessionLost();
      throw new ApiError(401, 'Session terminée. Reconnectez-vous.');
    }
  }

  if (response.status === 204) return undefined as T;

  const isJson = response.headers.get('content-type')?.includes('application/json') === true;
  const payload = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    const message =
      typeof payload === 'object' && payload !== null && 'error' in payload
        ? String((payload as { error: unknown }).error)
        : `Erreur ${response.status}`;
    const details =
      typeof payload === 'object' && payload !== null && 'details' in payload
        ? (payload as { details: unknown }).details
        : undefined;
    throw new ApiError(response.status, message, details);
  }

  return payload as T;
}

/** Télécharge un fichier servi par l'API, en gardant l'authentification. */
export async function apiDownload(path: string, filename: string): Promise<void> {
  const headers: Record<string, string> = {};
  if (accessToken !== null) headers['authorization'] = `Bearer ${accessToken}`;

  const response = await fetch(path, { headers, credentials: 'include' });
  if (!response.ok) {
    throw new ApiError(response.status, 'Export impossible.');
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
